import { useEffect, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import { type OperationState, UrqlClientError } from '@/src/integrations/urql';

import { logGraphqlErrorsToClientErrorLogger } from './logGraphqlErrorsToClientErrorLogger';

interface UseGraphQLResponseMessageOptions {
  errorMessage?: string;
  successMessage?: string;
}

const DEFAULT_OPTIONS = {
  errorMessage: 'Something went wrong.',
};

/**
 * A custom hook that can be used to display error or success messages in response to a GraphQL
 * operation.
 *
 * @param {OperationState} operationState The state of the GraphQL operation.
 * @param {UseGraphQLResponseMessageOptions} options
 *   Options that define the error and/or success messages that should be used.
 *
 * @deprecated
 * Please use the mutation factory in `@/src/integrations/urql/mutations/factory.ts` to wrap
 * mutations and perform the toast notifications imperatively inside of the hook that is wrapped by
 * the factory.
 */
export const useGraphQLResponseMessage = (
  operationState: OperationState,
  options?: UseGraphQLResponseMessageOptions,
): void => {
  const { enqueueSnackbar } = useSnackbar();

  const { errorMessage, successMessage } = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      ...options,
    }),
    [options],
  );

  useEffect(() => {
    if (!operationState.stale && operationState.error && !operationState.fetching) {
      const clientError = new UrqlClientError(operationState.error);
      const message =
        clientError.details.find(detail => detail.clientSafeMessage !== null)?.clientSafeMessage ??
        errorMessage;
      logGraphqlErrorsToClientErrorLogger(message, operationState.error, operationState);
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  }, [operationState, errorMessage, enqueueSnackbar]);

  useEffect(() => {
    if (
      !operationState.stale &&
      !operationState.error &&
      successMessage !== undefined &&
      operationState.data !== undefined
    ) {
      enqueueSnackbar(successMessage, {
        variant: 'success',
      });
    }
  }, [operationState, successMessage, enqueueSnackbar]);
};
